import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import RefreshIcon from '@mui/icons-material/Refresh'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import { Autocomplete, Button, Dialog, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import type { ProspectoDTO } from '../../../model/kartera-api-model'
import { lookupName } from '../../../utils/lookups'
import * as Permisos from '../../../utils/Permisos'
import ProspectForm from '../../prospects/form/prospect-form'
import { DESCRIPCION_MAX, RamoMark, selectMenu } from '../common/opportunity-common'
import { useOpportunityDrawerModel } from './opportunity-drawer-model'
import type { OpportunityDrawerProps } from './opportunity-drawer-model'

export type { OpportunityDrawerProps } from './opportunity-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const newProspectBtnSx: SxProps<Theme> = {
    width: 52,
    minWidth: 52,
    height: 52,
    padding: 0,
    flex: 'none',
    marginTop: 'calc(15px * 1.25 + 8px)',
    borderRadius: '12px',
    boxShadow: 'none',
    border: 0,
    backgroundColor: 'rgb(var(--k-accent-soft))',
    color: 'rgb(var(--k-accent-ink))',
    '& .MuiSvgIcon-root': { color: 'inherit' },
    '&:hover': {
        backgroundColor: 'rgb(var(--k-accent-soft))',
        filter: 'brightness(0.97)',
        boxShadow: 'none',
    },
}

const OpportunityDrawer = (props: OpportunityDrawerProps) => {
    const { open, onClose, onDelete, columns } = props
    const model = useOpportunityDrawerModel(props)
    const { form, change, card, status, prospectoLookup } = model

    return (
        <>
            <Drawer open={open} onClose={onClose} bare width={640}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={model.title}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5 pb-4">
                        <div className="flex items-start gap-3.5">
                            <RamoMark ramo={card?.ramo} boxed large />
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{model.title}</h2>
                                {card && status && (
                                    <div className="flex flex-wrap items-center gap-2 text-[13px] text-k-muted">
                                        <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${status.className}`}>{status.label}</span>
                                        {card.prospecto && <span>{lookupName(card.prospecto)}</span>}
                                        {card.dias != null && <span>{card.dias} días en el embudo</span>}
                                    </div>
                                )}
                                {!card && <span className="text-[13px] text-k-muted">Registra una oportunidad de venta con un prospecto.</span>}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {card && !model.loading && (
                            <div className="flex flex-wrap gap-2">
                                <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                                    {model.isOpen ? (
                                        <>
                                            <button type="button" className={`${actionClass} text-k-ok hover:bg-k-ok-soft`} disabled={model.saving} onClick={() => model.changeStatus('exitosa')}>
                                                <ThumbUpOutlinedIcon style={{ fontSize: 15 }} />Cerrar como ganada
                                            </button>
                                            <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} disabled={model.saving} onClick={() => model.changeStatus('no_exitosa')}>
                                                <ThumbDownOutlinedIcon style={{ fontSize: 15 }} />Cerrar como perdida
                                            </button>
                                        </>
                                    ) : (
                                        <button type="button" className={actionClass} disabled={model.saving} onClick={() => model.changeStatus('abierta')}>
                                            <MeetingRoomOutlinedIcon style={{ fontSize: 15 }} />Reabrir
                                        </button>
                                    )}
                                </GrantContent>
                                {onDelete && (
                                    <GrantContent permisos={[Permisos.PROSPECCION_D]}>
                                        <button type="button" className={`${actionClass} ml-auto text-k-danger-ink hover:bg-k-danger-soft`} onClick={() => onDelete(card.id)}>
                                            <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                        </button>
                                    </GrantContent>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {model.loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando oportunidad...</p>
                            </div>
                        )}

                        {model.message && (
                            <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                                <ErrorOutlineIcon style={{ fontSize: 18 }} />
                                <p className="m-0">{model.message.message}</p>
                            </div>
                        )}

                        {!model.loading && (
                            <form id="opportunity-form" onSubmit={model.save} className="flex flex-col gap-5 rounded-2xl border border-k-line bg-k-surface px-[18px] py-5">
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    required
                                    label="Nombre de la oportunidad"
                                    placeholder="Ej. GMM familiar 2027"
                                    value={form.nombre}
                                    onChange={(event) => change.nombre(event.target.value)}
                                />
                                <div className="flex items-start gap-2.5">
                                    <Autocomplete<ProspectoDTO>
                                        className="min-w-0 flex-1"
                                        options={prospectoLookup.options}
                                        loading={prospectoLookup.searching}
                                        value={form.prospecto}
                                        inputValue={prospectoLookup.inputValue}
                                        onInputChange={(_, next, reason) => prospectoLookup.changeInput(next, reason)}
                                        onChange={(_, option) => change.prospecto(option)}
                                        getOptionLabel={(option) => lookupName(option)}
                                        isOptionEqualToValue={(option, current) => option.id === current.id}
                                        filterOptions={(options) => options}
                                        noOptionsText={prospectoLookup.noOptionsText}
                                        loadingText="Buscando..."
                                        slotProps={{ paper: { className: 'v2-select-menu' } }}
                                        renderInput={(params) => (
                                            <TextField {...params} className="v2-field" variant="filled" fullWidth required label="Prospecto" helperText="Busca por nombre (mín. 3 letras)." />
                                        )}
                                    />
                                    <GrantContent permisos={[Permisos.PROSPECTOS_W]}>
                                        <Button type="button" variant="contained" aria-label="Nuevo prospecto" title="Nuevo prospecto" onClick={model.openProspectDialog} sx={newProspectBtnSx}>
                                            <PersonAddAlt1Icon style={{ fontSize: 20 }} />
                                        </Button>
                                    </GrantContent>
                                </div>
                                <div className="grid grid-cols-1 items-start gap-x-5 gap-y-5 sm:grid-cols-2">
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth
                                        select
                                        required
                                        label="Ramo"
                                        value={form.ramoId === '' ? '' : String(form.ramoId)}
                                        onChange={(event) => change.ramo(event.target.value ? Number(event.target.value) : '')}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                                        {model.ramos.map((ramo) => (
                                            <MenuItem key={ramo.id} value={String(ramo.id)}>
                                                <span className="flex items-center gap-2"><RamoMark ramo={ramo.ramo} />{ramo.ramo}</span>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth
                                        select
                                        required
                                        label="Etapa"
                                        value={form.columnId === '' ? '' : String(form.columnId)}
                                        onChange={(event) => change.column(event.target.value ? Number(event.target.value) : '')}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                                        {columns.map((column) => <MenuItem key={column.id} value={String(column.id)}>{column.nombre}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth
                                        select
                                        required
                                        label="Moneda"
                                        value={form.moneda}
                                        onChange={(event) => change.moneda(event.target.value)}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                                        {model.monedas.map((moneda) => (
                                            <MenuItem key={moneda.id} value={moneda.abreviacion || ''}>{moneda.abreviacion} · {moneda.moneda}</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth
                                        required
                                        label="Prima total estimada"
                                        value={form.primaTotal}
                                        onChange={(event) => change.primaTotal(event.target.value)}
                                        slotProps={{
                                            input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                            htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } },
                                        }}
                                    />
                                </div>
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    required
                                    multiline
                                    minRows={4}
                                    label="Descripción"
                                    value={form.descripcion}
                                    helperText={`${form.descripcion.length}/${DESCRIPCION_MAX}`}
                                    onChange={(event) => change.descripcion(event.target.value)}
                                />
                            </form>
                        )}

                        {card && !model.loading && (
                            <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">Actividad ({model.notes.length})</h3>
                                <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                                    <div className="flex flex-col gap-2">
                                        <label className="flex flex-col">
                                            <span className="sr-only">Nueva actividad</span>
                                            <textarea
                                                rows={2}
                                                value={model.newNote}
                                                placeholder="¿Qué avanzaste con el prospecto?"
                                                className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink"
                                                onChange={(event) => model.setNewNote(event.target.value)}
                                            />
                                        </label>
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                disabled={!model.newNote.trim() || model.saving}
                                                className="h-10 rounded-[10px] border-0 bg-k-primary px-3.5 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:bg-k-line disabled:text-k-muted-2"
                                                onClick={model.addNote}
                                            >
                                                Agregar actividad
                                            </button>
                                        </div>
                                    </div>
                                </GrantContent>
                                {model.notes.length === 0 && <p className="m-0 text-sm italic text-k-muted">Sin actividad registrada.</p>}
                                {model.notes.map((note) => (
                                    <div key={note.id} className="flex flex-col gap-1.5 rounded-xl border border-k-line bg-k-bg px-3 py-2.5">
                                        {model.editingNote?.id === note.id ? (
                                            <>
                                                <textarea
                                                    rows={2}
                                                    value={model.editingNote.texto}
                                                    className="resize-y rounded-lg border border-k-line-strong bg-k-surface px-2.5 py-2 text-sm text-k-ink"
                                                    onChange={(event) => model.changeEditingNote(event.target.value)}
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <button type="button" className="h-9 rounded-[10px] border border-k-line bg-transparent px-3 text-[13px] font-semibold text-k-ink" onClick={model.cancelEditNote}>Cancelar</button>
                                                    <button type="button" disabled={!model.editingNote.texto.trim() || model.saving} className="h-9 rounded-[10px] border-0 bg-k-primary px-3 text-[13px] font-bold text-white disabled:opacity-40" onClick={model.updateNote}>Guardar</button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className="m-0 whitespace-pre-line break-words text-sm text-k-ink-2">{note.texto}</p>
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-xs tabular-nums text-k-muted">{note.fecha}</span>
                                                    <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                                                        <button type="button" aria-label="Editar actividad" className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-k-muted hover:bg-k-hover" onClick={() => model.startEditNote(note.id, note.texto)}>
                                                            <EditOutlinedIcon style={{ fontSize: 15 }} />
                                                        </button>
                                                    </GrantContent>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </section>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                        <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                            <button
                                type="submit"
                                form="opportunity-form"
                                disabled={model.saving || model.loading}
                                className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {model.saving ? 'Guardando…' : model.isNew ? 'Crear oportunidad' : 'Guardar cambios'}
                            </button>
                        </GrantContent>
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.prospectDialog} onClose={model.closeProspectDialog} fullWidth maxWidth="md" slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Alta de prospecto</DialogTitle>
                <DialogContent>
                    <ProspectForm modal onCompleted={model.prospectCreated} />
                </DialogContent>
            </Dialog>
        </>
    )
}

export default OpportunityDrawer
